import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { products } from '../data/products';
import { uiTransition } from '../utils/motion';
const primaryLinks = [{
  to: '/platform',
  label: 'Platform'
}, {
  to: '/brand',
  label: 'Brand Portal'
}, {
  to: '/investor',
  label: 'Investor'
}];
export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const productsButtonRef = useRef<HTMLButtonElement>(null);
  const productsWrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const onProductPage = location.pathname.startsWith('/products/');
  useEffect(() => {
    setMenuOpen(false);
    setProductsOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!productsOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setProductsOpen(false);
        productsButtonRef.current?.focus();
      }
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (!productsWrapRef.current?.contains(event.target as Node)) {
        setProductsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handlePointerDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handlePointerDown);
    };
  }, [productsOpen]);
  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  const openProducts = () => {
    window.clearTimeout(closeTimer.current);
    setProductsOpen(true);
  };
  const scheduleCloseProducts = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setProductsOpen(false), 120);
  };
  const linkClass = (to: string) => `whitespace-nowrap text-sm font-semibold transition-colors hover:text-ink/60 ${location.pathname === to ? 'text-signal' : 'text-ink'}`;
  return <motion.header initial={reduceMotion ? false : {
    y: -64,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-paper/95 backdrop-blur-md" data-id="element-559" data-mp="7jb7tmdmi104">
      <nav className="mx-auto flex max-w-[1440px] items-center gap-6 px-5 py-4 md:px-10" aria-label="Main navigation" data-id="element-560" data-mp="15fcpk22d8w1">
        <Link to="/" className="flex items-center gap-2.5 text-ink transition-opacity hover:opacity-70" aria-label="Sampler home" data-id="element-561" data-mp="57iok9snafhu">
          <span className="grid h-8 w-8 place-items-center rounded-sm bg-ink text-paper" data-id="element-562" data-mp="xtld1urydf4i">
            <span className="h-2.5 w-2.5 rounded-full bg-current" data-id="element-563" data-mp="k2gkfcn05n3o" />
          </span>
          <span className="font-display text-xl font-bold" data-id="element-564" data-mp="o3ic98elawvm">SAMPLER</span>
        </Link>

        <div className="ml-auto hidden items-center gap-6 lg:flex" data-id="element-565" data-mp="1btw2quhj0no3">
          <div ref={productsWrapRef} className="relative" onMouseEnter={openProducts} onMouseLeave={scheduleCloseProducts} data-id="element-566" data-mp="1gkuo48hdgby7">
            <button ref={productsButtonRef} type="button" className={`flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold transition-colors hover:text-ink/60 ${onProductPage ? 'text-signal' : 'text-ink'}`} aria-expanded={productsOpen} aria-haspopup="true" aria-controls="products-menu" onClick={() => setProductsOpen(current => !current)} onFocus={openProducts} data-id="element-567" data-mp="3ozkma32n77kz">
              Products
              <ChevronDownIcon size={15} aria-hidden="true" className={`transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} data-id="element-568" data-mp="1vwn70o0p84k" />
            </button>

            <AnimatePresence data-id="element-569" data-mp="ufmtc7dnnvdn">
              {productsOpen && <motion.div id="products-menu" initial={reduceMotion ? false : {
              opacity: 0,
              y: -6,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={reduceMotion ? undefined : {
              opacity: 0,
              y: -6,
              scale: 0.98
            }} transition={uiTransition} style={{
              transformOrigin: 'top left'
            }} className="absolute left-0 top-full w-[320px] border border-ink/15 bg-paper p-2 shadow-[8px_8px_0_#11110f]" data-id="element-570" data-mp="plqwx9d1igkl">
                  <ul className="flex flex-col" data-id="element-571" data-mp="3evw6awpsmad9">
                    {products.map(product => <li key={product.slug} data-id="element-572" data-mp="1eto3yp90ghhf">
                        <Link to={product.path} className="flex gap-3 p-3 transition-colors hover:bg-ink/5" aria-current={location.pathname === product.path ? 'page' : undefined} data-id="element-573" data-mp="19xfn97f60g85">
                          <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${product.theme.dot}`} aria-hidden="true" data-id="element-574" data-mp="1tlevobm9bbae" />
                          <span data-id="element-575" data-mp="22md0gw4irmu1">
                            <span className="block whitespace-nowrap text-sm font-extrabold text-ink" data-id="element-576" data-mp="22j8pnfwjtsue">
                              {product.name}
                            </span>
                            <span className="mt-1 block text-xs leading-relaxed text-ink/60" data-id="element-577" data-mp="88vf9frl7226">
                              {product.summary}
                            </span>
                          </span>
                        </Link>
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>

          {primaryLinks.map(link => <Link key={link.to} to={link.to} className={linkClass(link.to)} aria-current={location.pathname === link.to ? 'page' : undefined} data-id="element-578" data-mp="21464iw12i9lq">
              {link.label}
            </Link>)}

          {isHome ? <a href="#start" className="whitespace-nowrap rounded-full bg-signal px-5 py-2.5 text-xs font-extrabold tracking-[0.08em] text-white transition-colors hover:bg-signal-dark active:bg-signal-darker" data-id="element-579" data-mp="1w538u26z9fqf">
              START A CAMPAIGN
            </a> : <Link to="/#start" className="whitespace-nowrap rounded-full bg-signal px-5 py-2.5 text-xs font-extrabold tracking-[0.08em] text-white transition-colors hover:bg-signal-dark active:bg-signal-darker" data-id="element-580" data-mp="2e9hcnt58ql7j">
              START A CAMPAIGN
            </Link>}
        </div>

        <button ref={menuButtonRef} type="button" className="ml-auto grid h-10 w-10 place-items-center rounded-full border border-ink/20 text-ink transition-colors hover:bg-ink/5 active:scale-95 lg:hidden" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-controls="mobile-navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen(current => !current)} data-id="element-581" data-mp="1zjta6olbpgi8">
          {menuOpen ? <XIcon size={19} aria-hidden="true" data-id="element-582" data-mp="3fzmjhmm624h6" /> : <MenuIcon size={19} aria-hidden="true" data-id="element-583" data-mp="3fwqkrw2hvbro" />}
        </button>
      </nav>

      <AnimatePresence data-id="element-584" data-mp="3axx5by3ndaov">
        {menuOpen && <motion.div id="mobile-navigation" initial={reduceMotion ? false : {
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? undefined : {
        opacity: 0,
        y: -8
      }} transition={uiTransition} className="max-h-[calc(100vh-4.5rem)] overflow-y-auto border-t border-ink/10 bg-paper px-5 py-4 lg:hidden" data-id="element-585" data-mp="3ikapx8o1d5e3">
            <nav className="flex flex-col gap-1" aria-label="Mobile navigation" data-id="element-586" data-mp="3ehns8r4ooq8z">
              <button type="button" className="flex items-center justify-between rounded-xl px-3 py-3 text-left text-base font-bold text-ink transition-colors hover:bg-ink/5" aria-expanded={mobileProductsOpen} aria-controls="mobile-products" onClick={() => setMobileProductsOpen(current => !current)} data-id="element-587" data-mp="ork8h4xoeamh">
                PRODUCTS
                <ChevronDownIcon size={17} aria-hidden="true" className={`transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} data-id="element-588" data-mp="lq0581c8qgjz" />
              </button>

              <AnimatePresence initial={false} data-id="element-589" data-mp="3u50c97g8aof7">
                {mobileProductsOpen && <motion.ul id="mobile-products" initial={reduceMotion ? false : {
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={reduceMotion ? undefined : {
              opacity: 0
            }} transition={uiTransition} className="ml-3 flex flex-col border-l border-ink/15 pl-3" data-id="element-590" data-mp="2z8qek9hhxy7">
                    {products.map(product => <li key={product.slug} data-id="element-591" data-mp="2p4iwd5i4uvko">
                        <Link to={product.path} className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold text-ink transition-colors hover:bg-ink/5" aria-current={location.pathname === product.path ? 'page' : undefined} data-id="element-592" data-mp="2l5taspmxy7py">
                          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${product.theme.dot}`} aria-hidden="true" data-id="element-593" data-mp="4g4of1668xnb" />
                          {product.name}
                        </Link>
                      </li>)}
                  </motion.ul>}
              </AnimatePresence>

              {primaryLinks.map(link => <Link key={link.to} to={link.to} className={`rounded-xl px-3 py-3 text-base font-bold transition-colors hover:bg-ink/5 ${location.pathname === link.to ? 'text-signal' : 'text-ink'}`} aria-current={location.pathname === link.to ? 'page' : undefined} data-id="element-594" data-mp="1ty1kt37hw3xt">
                  {link.label.toUpperCase()}
                </Link>)}

              {isHome ? <a href="#start" onClick={() => setMenuOpen(false)} className="mt-3 rounded-xl bg-signal px-4 py-3 text-center text-sm font-extrabold text-white transition-colors hover:bg-signal-dark active:bg-signal-darker" data-id="element-595" data-mp="118m9ecbx491d">
                  START A CAMPAIGN
                </a> : <Link to="/#start" className="mt-3 rounded-xl bg-signal px-4 py-3 text-center text-sm font-extrabold text-white transition-colors hover:bg-signal-dark active:bg-signal-darker" data-id="element-596" data-mp="13mho4ogtifvr">
                  START A CAMPAIGN
                </Link>}
            </nav>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}